import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "antd";
import { UserOutlined, CloseOutlined } from "@ant-design/icons";
import axiosInstance from "../services/axios";
import vaidehiRanade from "../assets/images/panchayatAdmins/vaidehiRanade.svg";
import parikshithYadav from "../assets/images/panchayatAdmins/parikshithYadav.svg";
import vijayasinhYadav from "../assets/images/panchayatAdmins/vijayasinhYadav.png";
import sagarPatel from "../assets/images/panchayatAdmins/sagarPatel.svg";
import sekarAnushaya from "../assets/images/panchayatAdmins/sekarAnushaya.svg";
import pramodPanduranga from "../assets/images/panchayatAdmins/pramodPanduranga.png";
import sanjayTulasiRam from "../assets/images/panchayatAdmins/sanjayTulasiRam.svg";
import rayChandraHariDas from "../assets/images/panchayatAdmins/rayChandraHariDas.svg";
import ghanashyamPhulChand from "../assets/images/panchayatAdmins/ghanashyamPhulChand.png";
import krishnaDevShubash from "../assets/images/panchayatAdmins/krishnaDevShubash.svg";
import sharadSahebRav from "../assets/images/panchayatAdmins/sharadSahebRav.png";
import diptiAmitDharay from "../assets/images/panchayatAdmins/diptiAmitDharay.png";
import whatsappIcon from "../assets/icons/whatsapp.svg";
import phoneIcon from "../assets/icons/phone.svg";
import zillaParishad from "../assets/images/zillaParishad.svg";
import panchayatSamiti from "../assets/images/header/samithiLogo.png";
import "./LogoCard.css";

export default function LogoCard({ className = "" }) {
  const { t, i18n } = useTranslation();
  const [activePopup, setActivePopup] = useState(null);
  const [districtData, setDistrictData] = useState([]);
  const [panchayatData, setPanchayatData] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingDistrict, setLoadingDistrict] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingPanchayat, setLoadingPanchayat] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [errorDistrict, setErrorDistrict] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [errorPanchayat, setErrorPanchayat] = useState(null);

  // Fetch Zilla Parishad officers with tags=district
  useEffect(() => {
    let isMounted = true;
    const languageParam = i18n.language === "mr" ? "Marathi" : "English";
    setLoadingDistrict(true);

    axiosInstance
      .get("/administration/", {
        params: { language: languageParam, tags: "District" },
      })
      .then((resp) => {
        if (!isMounted) return;
        let data = resp && resp.data ? resp.data : [];
        if (!Array.isArray(data)) {
          data = data.results || data.data || Object.values(data) || [];
        }

        if (!Array.isArray(data) || data.length === 0) {
          setDistrictData([]);
          return;
        }

        // If `order` (or similar) exists on all items, sort by it; otherwise preserve API order
        const orderKeyCandidates = ["order", "sort_order", "position"];
        const orderKey = orderKeyCandidates.find((k) =>
          data.every((item) => item[k] !== undefined)
        );
        const ordered = orderKey
          ? [...data].sort(
              (a, b) => (Number(a[orderKey]) || 0) - (Number(b[orderKey]) || 0)
            )
          : data;

        const mapped = ordered.map((d) => ({
          id: d.id,
          name: d.name || "-",
          title: d.title || d.designation || "-",
          avatarUrl: d.image || d.avatarUrl || null,
          contact: d.contact || null,
          whatsapp: d.whatsapp || null,
          tags: d.tags || null,
          language: d.language || null,
        }));

        setDistrictData(mapped.length ? mapped : []);
      })
      .catch((err) => {
        console.error("Failed to load Zilla Parishad data:", err);
        if (isMounted) setErrorDistrict(err.message || "Failed to load data");
      })
      .finally(() => {
        if (isMounted) setLoadingDistrict(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Fetch Panchayat Samiti officers with tags=panjayat
  useEffect(() => {
    let isMounted = true;
    const languageParam = i18n.language === "mr" ? "Marathi" : "English";
    setLoadingPanchayat(true);

    axiosInstance
      .get("/administration/", {
        params: { language: languageParam, tags: "Panchayat" },
      })
      .then((resp) => {
        if (!isMounted) return;
        let data = resp && resp.data ? resp.data : [];
        if (!Array.isArray(data)) {
          data = data.results || data.data || Object.values(data) || [];
        }

        if (!Array.isArray(data) || data.length === 0) {
          setPanchayatData([]);
          return;
        }

        // If `order` (or similar) exists on all items, sort by it; otherwise preserve API order
        const orderKeyCandidates = ["order", "sort_order", "position"];
        const orderKey = orderKeyCandidates.find((k) =>
          data.every((item) => item[k] !== undefined)
        );
        const ordered = orderKey
          ? [...data].sort(
              (a, b) => (Number(a[orderKey]) || 0) - (Number(b[orderKey]) || 0)
            )
          : data;

        const mapped = ordered.map((d) => ({
          id: d.id,
          name: d.name || "-",
          title: d.title || d.designation || "-",
          avatarUrl: d.image || d.avatarUrl || null,
          contact: d.contact || null,
          whatsapp: d.whatsapp || null,
          tags: d.tags || null,
          language: d.language || null,
        }));

        setPanchayatData(mapped.length ? mapped : []);
      })
      .catch((err) => {
        console.error("Failed to load Panchayat Samiti data:", err);
        if (isMounted) setErrorPanchayat(err.message || "Failed to load data");
      })
      .finally(() => {
        if (isMounted) setLoadingPanchayat(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);
  
  const logoCardData = [
    {
      id: 1,
      logo: zillaParishad,
      altText: "Zilla Parishad Logo",
      titleKey: "Zilla Parishad Ratnagiri",
      fallbackTitle: "जिल्हा परिषद रत्नागिरी"
    },
    {
      id: 2,
      logo: panchayatSamiti,
      altText: "Panchayat Samiti Logo", 
      titleKey: "Panchayat Samiti Guhagar",
      fallbackTitle: "पंचायत समिती गुहागर"
    }
  ];

  // Language-specific administrative data for popups
  const zillaParishadOfficersMr = [
    {
      id: 1,
      name: "सौ.वैदही रानडे",
      title: "मुख्य कार्यकारी अधिकारी (CEO)",
      avatarUrl: vaidehiRanade
    },
    {
      id: 2,
      name: "श्री. परिक्षीत यादव",
      title: "अतिरिक्य मुख्य कार्यकारी अधिकारी  (Add.CEO)",
      avatarUrl: parikshithYadav
    },
    {
      id: 3,
      name: "श्री.विजयसिंह जाधव",
      title: "उपमुख्यकार्यकारी अधिकारी (Dy CEO, GAD)",
      avatarUrl: vijayasinhYadav
    },
    {
      id: 4,
      name: "श्री.सागर पाटील",
      title: "उपमुख्य कार्यकारी अधिकारी (Dy CEO, VP)",
      avatarUrl: sagarPatel
    }
  ];

  const zillaParishadOfficersEn = [
    {
      id: 1,
      name: "Mrs. Vaidahi Ranade",
      title: "Chief Executive Officer (CEO)",
      avatarUrl: vaidehiRanade
    },
    {
      id: 2,
      name: "Mr. Parikshit Yadav",
      title: "Additional Chief Executive Officer (Add.CEO)",
      avatarUrl: parikshithYadav
    },
    {
      id: 3,
      name: "Mr. Vijaysinh Jadhav",
      title: "Deputy Chief Executive Officer (Dy CEO, GAD)",
      avatarUrl: vijayasinhYadav
    },
    {
      id: 4,
      name: "Mr. Sagar Patil",
      title: "Deputy Chief Executive Officer (Dy CEO, VP)",
      avatarUrl: sagarPatel
    }
  ];

  const panchayatSamitiOfficersMr = [
    {
      id: 1,
      name: "शेखर अनुसया शामराव भिलारे",
      title: "गटविकास अधिकारी (BDO)",
      contact: "+91 7620639305",
      whatsapp: "+91 7620639305",
      avatarUrl: sekarAnushaya
    },
    {
      id: 2,
      name: "प्रमोद पांडुरंग केळसकर",
      title: "सहाय्यक गटविकास अधिकारी (ABDO)+CDPO",
      contact: "+91 9422593673",
      whatsapp: "+91 9422593673",
      avatarUrl: pramodPanduranga
    },
    {
      id: 3,
      name: "संजय तुळशीराम सळमाखे",
      title: "उपअभियंता, बांधकाम (Dy.Engg)",
      contact: "+91 9423373973",
      whatsapp: "+91 9423373973",
      avatarUrl: sanjayTulasiRam
    },
    {
      id: 4,
      name: "सौ.दिप्ती अमित धारप",
      title: "उपअभियंता, पाणीपुरवठा (Dy.Engg)",
      contact: "+91 9421229884",
      whatsapp: "+91 9421229884",
      avatarUrl: diptiAmitDharay
    },
    {
      id: 5,
      name: "श्री. रायचंद्र हरिदास गळवे",
      title: "गटशिक्षण अधिकारी (BEO)",
      contact: "+91 8275265930",
      whatsapp: "+91 8275265930",
      avatarUrl: rayChandraHariDas
    },
    {
      id: 6,
      name: "डॉ घनश्याम फुलचंद जांगीड",
      title: "तालुका आरोग्य अधिकारी (THO)",
      contact: "+91 7057405776",
      whatsapp: "+91 7057405776",
      avatarUrl: ghanashyamPhulChand
    },
    {
      id: 7,
      name: "कृष्णदेव सुभाष कदम",
      title: "कृषी अधिकारी (AO)",
      contact: "+91 9145720282",
      whatsapp: "+91 9145720282",
      avatarUrl: krishnaDevShubash
    },
    {
      id: 8,
      name: "श्री शरद साहेबराव भांड",
      title: "विस्तार अधिकारी (EO), ग्रामपंचायत",
      contact: "+91 8275271288",
      whatsapp: "+91 8275271288",
      avatarUrl: sharadSahebRav
    },
  ];

  const panchayatSamitiOfficersEn = [
    {
      id: 1,
      name: "Shekhar Anusya Shamrao Bhilare",
      title: "Block Development Officer (BDO)",
      contact: "+91 7620639305",
      whatsapp: "+91 7620639305",
      avatarUrl: sekarAnushaya
    },
    {
      id: 2,
      name: "Pramod Pandurang Kelaskar",
      title: "Assistant Block Development Officer (ABDO)+CDPO",
      contact: "+91 9422593673",
      whatsapp: "+91 9422593673",
      avatarUrl: pramodPanduranga
    },
    {
      id: 3,
      name: "Sanjay Tulshiram Salmakhe",
      title: "Deputy Engineer, Construction (Dy.Engg)",
      contact: "+91 9423373973",
      whatsapp: "+91 9423373973",
      avatarUrl: sanjayTulasiRam
    },
    {
      id: 4,
      name: "Ms. Deepti Amit Dharap",
      title: "Deputy Engineer, Water Supply (Dy.Engg)",
      contact: "+91 9421229884",
      whatsapp: "+91 9421229884",
      avatarUrl: diptiAmitDharay
    },
    {
      id: 5,
      name: "Mr. Raichandra Haridas Galve",
      title: "Block Education Officer (BEO)",
      contact: "+91 8275265930",
      whatsapp: "+91 8275265930",
      avatarUrl: rayChandraHariDas
    },
    {
      id: 6,
      name: "Dr. Ghanshyam Phulchand Jangid",
      title: "Taluka Health Officer (THO)",
      contact: "+91 7057405776",
      whatsapp: "+91 7057405776",
      avatarUrl: ghanashyamPhulChand
    },
    {
      id: 7,
      name: "Krishnadev Subhash Kadam",
      title: "Agriculture Officer (AO)",
      contact: "+91 9145720282",
      whatsapp: "+91 9145720282",
      avatarUrl: krishnaDevShubash
    },
    {
      id: 8,
      name: "Mr. Sharad Sahebrao Bhand",
      title: "Extension Officer (EO), Gram Panchayat",
      contact: "+91 8275271288",
      whatsapp: "+91 8275271288",
      avatarUrl:  sharadSahebRav
    },
  ];

  const handleCardClick = (cardId) => {
    setActivePopup(cardId);
  };

  const handleClosePopup = () => {
    setActivePopup(null);
  };

  const getOfficersData = (cardId) => {
    if (cardId === 1) {
      // Return API data if available, otherwise fallback to static data
      if (districtData.length > 0) {
        return districtData;
      }
      return i18n.language === 'mr' ? zillaParishadOfficersMr : zillaParishadOfficersEn;
    }
    if (cardId === 2) {
      // Return API data if available, otherwise fallback to static data
      if (panchayatData.length > 0) {
        return panchayatData;
      }
      return i18n.language === 'mr' ? panchayatSamitiOfficersMr : panchayatSamitiOfficersEn;
    }
    return [];
  };

  const getPopupTitle = (cardId) => {
    return cardId === 1 ? "जिल्हा परिषद रत्नागिरी" : "पंचायत समिती गुहागर";
  };

  return (
    <div className={`gap-2 logo-card-section max-sm:gap-1 ${className}`}>
      <div className="logo-cards-container">
        {logoCardData.map((item) => (
          <div key={item.id} className="logo-card-item" onClick={() => handleCardClick(item.id)}>
            <div className="district-card">
              <div
                className="district-content flex flex-col max-sm:gap-2 max-sm:items-stretch max-sm:text-center sm:flex-row sm:items-center w-full gap-4"
                style={{ padding: 16 }}
              >
                <div className="district-logo-wrap flex-shrink-0 flex items-center justify-center max-sm:mb-2" style={{ height: '100%' }}>
                  <img
                    src={item.logo}
                    alt={item.altText}
                    className="district-logo"
                    style={{ height: 48, width: 48, minWidth: 48, minHeight: 48 }}
                  />
                </div>
                <span
                  className="district-title"
                  style={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    textAlign: 'left',
                    flex: 1,
                    fontWeight: 600,
                    fontSize: 18,
                    margin: 0,
                    padding: 0
                  }}
                >
                  {t(item.titleKey) || item.fallbackTitle}
                </span>
                <button
                  className="district-button max-sm:mt-2 max-sm:w-full"
                  style={{
                    flexShrink: 0,
                    marginLeft: 12,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {t('See All Officers') || 'सर्व पदाधिकारी पहा'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Popup Modal */}
      {activePopup && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <div className="popup-container" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="popup-header">
              <div className="popup-header-content">
                <img loading="lazy" 
                  src={logoCardData.find(card => card.id === activePopup)?.logo} 
                  alt="Logo" 
                  className="popup-logo"
                />
                <h2 className="popup-title">{getPopupTitle(activePopup)}</h2>
              </div>
              <button className="popup-close-btn" onClick={handleClosePopup}>
                <CloseOutlined />
              </button>
            </div>

            {/* Subtitle - Only for Zilla Parishad */}
            {activePopup === 1 && (
              <>
                <p className="popup-subtitle">सरकारी ग्राधिकरण पदानुक्रम</p>
                <p className="popup-description">
                  तुमच्या लोकप्रतिनिधींना आणि प्रमुख प्रशासकीय अधिकाऱ्यांना जाणून घ्या
                </p>
              </>
            )}

            {/* Officers List */}
            <div className="popup-officers-list">
              {getOfficersData(activePopup).map((admin, index) => (
                <div
                  key={admin.id || index}
                  className="admin-relative admin-flex admin-flex-col admin-w-full max-w-md popup-officer-item"
                >
                  {/* Officer Card */}
                  <div className="admin-flex admin-w-full admin-max-w-md align-items-center">
                    <Avatar
                      size={activePopup === 1 ? 47 : 90}
                      src={admin.avatarUrl}
                      icon={<UserOutlined />}
                      className={`logocard-avatar-${activePopup === 1 ? 'zilla' : 'panchayat'}`}
                    />
                    <div className="admin-flex admin-flex-col admin-justify-center admin-ml-negative-50 admin-border admin-border-white admin-bg-white admin-pl-16 admin-rounded-full admin-w-full admin-max-w-md">
                      <span className="text-[14px] text-[#212121] font-semibold">
                        {admin.name}
                      </span>
                      <span className="admin-text-gray mb-2 admin-w-full text-[12px] mt-1 text-[#727272] font-medium leading-20">
                        {admin.title}
                      </span>

                      {/* Contact Buttons - Only for Panchayat Samiti */}
                      {activePopup === 2 && (admin.contact || admin.whatsapp) && (
                        <div className="officer-contact-buttons">
                          {admin.whatsapp && (
                            <button 
                              className="whatsapp-btn"
                              onClick={() => {
                                const waNumber = admin.whatsapp.replace(/[^0-9]/g, "");
                                const waMessage = encodeURIComponent("Hello");
                                window.open(`https://wa.me/${waNumber}?text=${waMessage}`, '_blank');
                              }}
                            >
                              <img loading="lazy" src={whatsappIcon} alt="WhatsApp" className="media-contact-icon" />
                              व्हाट्सअप<span className="mobile-hide-text"> करा</span>
                            </button>
                          )}
                          {admin.contact && (
                            <button 
                              className="call-btn"
                              onClick={() => {
                                const phone = admin.contact.replace(/[^0-9]/g, "");
                                window.open(`tel:${phone}`, '_self');
                              }}
                            >
                              <img loading="lazy" src={phoneIcon} alt="Call" className="media-contact-icon" />
                              फोन<span className="mobile-hide-text"> करा</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index !== getOfficersData(activePopup).length - 1 && (
                    <div className="admin-top-full admin-w-2-5 admin-h-7 admin-bg-black admin-self-center" />
                  )}
                </div>
              ))}
            </div>

            {/* Footer Button - Only for Zilla Parishad */}
            {activePopup === 1 && (
              <div className="popup-footer">
                <button 
                  className="popup-footer-btn"
                  onClick={() => window.open('https://zpratnagiri.gov.in/', '_blank')}
                >
                  जिल्हा परिषद रत्नागिरी अधिकाऱ्यांचे वेबसाइट
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
